import React, { FC, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { Form } from 'react-final-form';
import { useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';

import LoadingSpinner from 'src/common/components/LoadingSpinner';
import useAppDispatch from 'src/common/hooks/useAppDispatch';
import type { ReduxState } from '../../../../types/reduxState';
import { PAGES } from '../../../constants';
import useOnSubmitLocationChange from '../../../../common/hooks/useOnSubmitLocationChange';
import { updateUser } from '../../../../common/ducks/user';
import ContactDetails from '../../../../common/components/fieldsets/ContactDetails';
import MarketingConsent from '../../../../common/components/fieldsets/MarketingConsent';
import FormError from '../../../../common/components/FormError';
import Button from '../../../../common/components/Button';
import CloseButton from '../../../../common/components/CloseButton';
import getReturnUrl from '../../../../common/utils/getReturnUrl';
import SignInOrSwitchAccounts from '../../../../common/components/SignInOrSwitchAccounts';
import s from './Contact.module.scss';

// We want initialValues to be undefined
// if there's no data to be mapped into it,
// rather than initializing individual values
// to undefined, which will clear data.
const getContactInitialValues = ({ user }: ReduxState) => (user.get('email')
  ? {
    first_name: user.get('first_name'),
    last_name: user.get('last_name'),
    email: user.get('email'),
    phone: user.get('phone'),
    marketing_consent: user.get('marketing_consent'),
  }
  : undefined);

const Contact: FC = () => {
  const location = useLocation();
  const dispatch = useAppDispatch();
  const returnUrl = getReturnUrl(location);
  const initialValues = useSelector(getContactInitialValues);

  const goToPasswordPage = useOnSubmitLocationChange(PAGES.PASSWORD);

  const onSubmit = useCallback(
    (values) => {
      // Store user data in redux state so that we can submit it
      // along with the password on the next page.
      dispatch(updateUser(values));
      goToPasswordPage();
    },
    [dispatch, goToPasswordPage],
  );

  return (
    <div className={s.container}>
      <Helmet>
        <title>Contact</title>
      </Helmet>
      <h1 className={s.title}>Your Contact Details</h1>
      <div className={s.message}>
        <SignInOrSwitchAccounts />
      </div>
      <Form
        onSubmit={onSubmit}
        initialValues={initialValues}
      >
        {({ handleSubmit, submitError, submitting, submitSucceeded }) => (
          <form
            method="POST"
            onSubmit={handleSubmit}
            className={s.form}
          >
            <div>
              {submitError && (
                <FormError
                  error={submitError}
                  className={s.error}
                />
              )}
              <ContactDetails isLoggedIn={false} />
              <MarketingConsent />
            </div>

            <Button
              type="submit"
              data-test="next"
              fullWidth
              disabled={submitting || submitSucceeded}
            >
              {(submitting || submitSucceeded) ? (
                <LoadingSpinner spinnerOnly variant="s" />
              ) : 'Next'}
            </Button>
          </form>
        )}
      </Form>
      <CloseButton backUrl={returnUrl} />
    </div>
  );
};

export default Contact;
